import { z } from 'zod';
/**
 * Zod schemas for the Communication routes. Per-kind config validation is
 * done in the service layer (telegram needs botToken, email needs imapHost,
 * etc.) — here we only enforce shape-level safety.
 */
export const CommunicationChannelKindSchema = z.enum([
    'telegram',
    'facebook',
    'instagram',
    'whatsapp',
    'email_imap',
]);
export const CommunicationConversationStatusSchema = z.enum([
    'open',
    'pending',
    'resolved',
    'closed',
]);
export const CreateCommunicationChannelRequestSchema = z.object({
    kind: CommunicationChannelKindSchema,
    label: z.string().min(1).max(120),
    externalId: z.string().min(1).max(200).optional(),
    // Free-form config — service validates per-kind required keys.
    config: z.record(z.string(), z.unknown()),
    isActive: z.boolean().optional(),
});
export const UpdateCommunicationChannelRequestSchema = z
    .object({
    label: z.string().min(1).max(120).optional(),
    externalId: z.string().min(1).max(200).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    isActive: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const GrantCommunicationChannelAccessRequestSchema = z.object({
    userId: z.string().uuid(),
});
export const SendCommunicationMessageRequestSchema = z.object({
    body: z.string().min(1).max(8000),
});
export const CommunicationConversationsQuerySchema = z.object({
    channelId: z.string().uuid().optional(),
    status: CommunicationConversationStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const CommunicationChannelIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const CommunicationConversationIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const CommunicationGrantUserParamSchema = z.object({
    channelId: z.string().uuid(),
    userId: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Calendar — scheduled messages
// ---------------------------------------------------------------------------
const EmailAddress = z
    .string()
    .trim()
    .min(3)
    .max(254)
    .email('Invalid email address');
const AttachmentStagedSchema = z.object({
    filename: z.string().min(1).max(260),
    mimeType: z.string().min(1).max(200),
    sizeBytes: z.number().int().min(0),
    storedPath: z.string().min(1).max(1024),
});
export const CreateCommunicationScheduledMessageRequestSchema = z
    .object({
    channelId: z.string().uuid(),
    body: z.string().min(1).max(20_000),
    emailTo: z.array(EmailAddress).max(200).optional(),
    emailCc: z.array(EmailAddress).max(200).optional(),
    emailBcc: z.array(EmailAddress).max(200).optional(),
    emailSubject: z.string().max(998).optional(),
    attachments: z.array(AttachmentStagedSchema).max(20).optional(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    sendNow: z.boolean().optional(),
})
    .refine((d) => d.sendNow === true || !!d.scheduledAt, {
    message: 'Either sendNow=true or scheduledAt must be provided',
});
export const UpdateCommunicationScheduledMessageRequestSchema = z
    .object({
    body: z.string().min(1).max(20_000).optional(),
    emailTo: z.array(EmailAddress).max(200).optional(),
    emailCc: z.array(EmailAddress).max(200).optional(),
    emailBcc: z.array(EmailAddress).max(200).optional(),
    emailSubject: z.string().max(998).optional(),
    attachments: z.array(AttachmentStagedSchema).max(20).optional(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    sendNow: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const CommunicationScheduledMessagesQuerySchema = z.object({
    channelId: z.string().uuid().optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const CommunicationScheduledMessageIdParamSchema = z.object({
    id: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Recipient lists
// ---------------------------------------------------------------------------
export const CreateCommunicationRecipientListRequestSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    emails: z.array(EmailAddress).max(2000),
});
export const UpdateCommunicationRecipientListRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(500).optional(),
    emails: z.array(EmailAddress).max(2000).optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const CommunicationRecipientListIdParamSchema = z.object({
    id: z.string().uuid(),
});
//# sourceMappingURL=communication.schema.js.map